<template>
  <div class="register-page-shell">
    <main class="register-page-main">
      <div class="register-page-card">
        <BrandLogo class="register-page-logo" />
        <!-- El alta es solo por invitación: sin una válida, se pide acceso. -->
        <RequestAccess v-if="mostrarSolicitud">
          <InvitationBanner :dead="invitationDead" />
        </RequestAccess>
        <template v-else>
          <h1 class="register-page-heading">{{ $t('invitation.heading') }}</h1>
          <InvitationBanner :preview="invitation" />

          <v-form ref="formRef" v-model="formValid" @submit.prevent="handleRegister">
            <v-text-field
              v-model="form.email"
              :label="$t('signup.email')"
              type="email"
              outlined
              dense
              persistent-placeholder
              autocomplete="email"
              :readonly="!!invitation"
              :rules="emailRules"
              class="register-page-input"
              hide-details="auto"
              data-test="register-email"
            />

            <v-text-field
              v-model="form.password"
              :label="$t('signup.password')"
              :type="showPassword ? 'text' : 'password'"
              outlined
              dense
              persistent-placeholder
              autocomplete="new-password"
              :rules="passwordRules"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              class="register-page-input"
              hide-details="auto"
              data-test="register-password"
              @click:append="showPassword = !showPassword"
            />
            <p class="register-page-hint">{{ $t('signup.passwordHint') }}</p>

            <v-checkbox
              v-model="form.acceptTerms"
              :rules="termsRules"
              hide-details
              class="register-page-terms"
            >
              <template #label>
                <span class="register-page-terms__label">
                  <i18n path="signup.accept" tag="span">
                    <template #terms>
                      <a href="https://ininvoice.com/terms" target="_blank" rel="noopener" class="register-page-link" @click.stop>{{ $t('signup.termsLink') }}</a>
                    </template>
                    <template #privacy>
                      <a href="https://ininvoice.com/privacy" target="_blank" rel="noopener" class="register-page-link" @click.stop>{{ $t('signup.privacyLink') }}</a>
                    </template>
                  </i18n>
                </span>
              </template>
            </v-checkbox>

            <TurnstileWidget ref="turnstile" @token="captchaToken = $event" />

            <p v-if="error" class="register-page-error" role="alert">{{ error }}</p>

            <v-btn
              type="submit"
              block
              depressed
              large
              :loading="loading"
              :disabled="!formValid || esperandoCaptcha"
              class="register-page-submit"
            >
              {{ $t('signup.submit') }}
            </v-btn>
          </v-form>

          <p class="register-page-footer">
            {{ $t('signup.haveAccount') }}
            <router-link :to="signInPath" class="register-page-link">
              {{ $t('signup.signIn') }}
              <span aria-hidden="true" class="register-page-arrow">↗</span>
            </router-link>
          </p>
        </template>

        <div class="register-page-lang">
          <span class="register-page-lang__label">{{ $t('langSelector.label') }}:</span>
          <a
            v-for="loc in locales"
            :key="loc"
            href="#"
            role="button"
            class="register-page-lang__link"
            :class="{ 'register-page-lang__link--active': $i18n.locale === loc }"
            :aria-pressed="String($i18n.locale === loc)"
            :aria-label="$t('langSelector.' + loc)"
            @click.prevent="onPickLocale(loc)"
          >{{ $t('langSelector.' + loc) }}</a>
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { supabase } from '../services/supabase';
import { api, createHandoffCode, buildHandoffUrl } from '../services/api';
import { estadoHttp, tipoDeNegativa } from '../services/apiErrors';
import { turnstileEncendido } from '../services/turnstile';
import {
  trackEvent,
  setAccessToken,
  gatherAttribution,
} from '../services/funnelApi';
import i18n, { setLocale, SUPPORTED_LOCALES, type AppLocale } from '../i18n';
import { okeyTrack, okeyTrackOnce } from '../utils/track';
import InvitationBanner from '../invitations/InvitationBanner.vue';
import { invitationLanding, invitationTokenFromUrl, previewInvitation, withInvitationInApp, type InvitationPreview } from '../invitations/invitation';
import { paramDeLaUrl } from '../utils/urlParams';
import BrandLogo from '@/components/brand/BrandLogo.vue';
import RequestAccess from '@/components/RequestAccess.vue';
import TurnstileWidget from '@/components/TurnstileWidget.vue';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type MotivoDeFallo = 'account_exists' | 'invitation_required' | 'captcha' | 'rate_limited' | 'server_error' | 'session_failed';

export default defineComponent({
  name: 'RegisterView',
  components: { BrandLogo, InvitationBanner, RequestAccess, TurnstileWidget },
  setup() {
    const t = (key: string): string => String(i18n.t(key));
    const loading = ref(false);
    const error = ref('');
    const formValid = ref(false);
    const showPassword = ref(false);
    const captchaToken = ref<string | null>(null);
    const turnstile = ref<{ reset?: () => void } | null>(null);
    const invitationToken = ref<string | null>(null);
    const invitation = ref<InvitationPreview | null>(null);
    const invitationDead = ref(false);
    // El backend dijo 403 INVITATION_REQUIRED: la invitación no vale para este alta.
    const invitationRequired = ref(false);
    // El registro es solo por invitación (punto 10): sin una válida no hay formulario.
    const mostrarSolicitud = computed(() => !invitationToken.value || invitationDead.value || invitationRequired.value);
    const esperandoCaptcha = computed(() => turnstileEncendido && !captchaToken.value);
    // El acceso recibe la misma invitación: la persona ya tenía cuenta.
    const signInPath = computed(() =>
      withInvitationInApp('/signin', invitationToken.value, invitation.value ? { email: invitation.value.email } : {})
    );

    const form = ref({
      email: '',
      password: '',
      acceptTerms: false,
    });

    const locales = SUPPORTED_LOCALES as readonly AppLocale[];

    const onPickLocale = (loc: AppLocale): void => {
      setLocale(loc);
    };

    // Rules guard against `undefined` on initial render (Vuetify validates
    // before any user input). Coerce to string/boolean before .length / regex.
    const emailRules = [
      (v: string | undefined) => !!v || t('signup.rules.emailRequired'),
      (v: string | undefined) => (typeof v === 'string' && EMAIL_RE.test(v)) || t('signup.rules.emailInvalid'),
    ];

    const passwordRules = [
      (v: string | undefined) => !!v || t('signup.rules.passwordRequired'),
      (v: string | undefined) => (typeof v === 'string' && v.length >= 8) || t('signup.rules.passwordMin'),
      (v: string | undefined) => (typeof v === 'string' && /[A-Z]/.test(v)) || t('signup.rules.passwordUpper'),
      (v: string | undefined) => (typeof v === 'string' && /[0-9]/.test(v)) || t('signup.rules.passwordDigit'),
    ];

    const termsRules = [
      (v: boolean | undefined) => v === true || t('signup.rules.mustAccept'),
    ];

    /**
     * Publica un fallo de alta como CATEGORÍA, nunca como texto.
     *
     * El mensaje del servidor puede arrastrar el email u otros datos del
     * usuario, así que no se envía: solo una etiqueta cerrada y el código HTTP.
     * Sin esto, un alta que revienta es indistinguible de una que nunca se
     * intentó — hoy `signup_started` sin `sign_up` no dice si el visitante se
     * fue o si el registro falló.
     */
    const emitSignupError = (reason: MotivoDeFallo, status?: number): void => {
      okeyTrack('signup_error', {
        reason,
        status: typeof status === 'number' ? status : 0,
        screen: 'register',
      });
    };

    // La cuenta ya existe: se entra con la sesión que el propio alta devolvió
    // (`/api/auth/signup/direct`, que la pide a Supabase con el mismo captcha) y se
    // hace el handoff a la app. Solo si el backend no la trajo se pide con la
    // contraseña recién elegida (sin captcha en Supabase, eso funciona).
    const completeSignIn = async (sesion?: { access_token?: string; refresh_token?: string } | null): Promise<void> => {
      const { data: signInData, error: signInErr } = sesion?.access_token && sesion?.refresh_token
        ? await supabase.auth.setSession({ access_token: sesion.access_token, refresh_token: sesion.refresh_token })
        : await supabase.auth.signInWithPassword({
          email: form.value.email,
          password: form.value.password,
        });
      if (signInErr || !signInData?.session) {
        error.value = t('signup.errors.session');
        // La cuenta puede haberse creado y aun así dejar al usuario fuera.
        // Sin este evento el caso es invisible: no hay sign_up y tampoco error.
        emitSignupError('session_failed');
        return;
      }
      const { access_token, refresh_token } = signInData.session;
      setAccessToken(access_token);
      await trackEvent({
        event: 'signup_completed',
        email: form.value.email,
        user_id: signInData.user?.id,
      });
      // sign_up: SOLO aquí. La cuenta existe y hay sesión válida devuelta por
      // Supabase; el early-return de arriba cubre el fallo. Nunca en submit ni
      // en click. Sin PII: el email y el user_id van al backend por trackEvent,
      // no a GA4 ni a PostHog.
      //
      // Se emite una sola vez por cuenta. El user_id se usa únicamente como
      // clave local de deduplicación en localStorage; NO viaja en el evento.
      okeyTrackOnce(signInData.user?.id ?? 'unknown', 'sign_up', { method: 'password' });
      // Handoff por código de un solo uso: ningún token viaja por la URL.
      const MAIN_APP_URL = process.env.VUE_APP_MAIN_APP_URL || 'http://localhost:8080';
      const handoffCode = await createHandoffCode(access_token, refresh_token || '');
      if (!handoffCode) {
        error.value = t('signup.errors.session');
        emitSignupError('session_failed');
        return;
      }
      // Solo se da de alta quien trae invitación: aterriza en su aceptación.
      const destino = invitationToken.value ? invitationLanding(invitationToken.value) : '/onboarding';
      window.location.href = buildHandoffUrl(MAIN_APP_URL, handoffCode, destino);
    };

    /** Siempre una frase de i18n y una categoría cerrada; nunca el texto del servidor. */
    const tratarNegativa = (err: unknown): void => {
      const status = estadoHttp(err);
      const tipo = tipoDeNegativa(err);
      if (status === 403 || tipo === 'INVITATION_REQUIRED') {
        invitationRequired.value = true;
        emitSignupError('invitation_required', status);
      } else if (status === 409) {
        // Sin enumerar: no se dice si la cuenta existe, solo a dónde ir si ya la tienes.
        error.value = t('signup.errors.alreadyHaveAccount');
        emitSignupError('account_exists', status);
      } else if (tipo === 'CAPTCHA') {
        error.value = t('signup.errors.captcha');
        emitSignupError('captcha', status);
      } else if (status === 429) {
        error.value = t('signup.errors.tooMany');
        emitSignupError('rate_limited', status);
      } else if (status === 422) {
        error.value = t('signup.errors.passwordWeak');
        emitSignupError('server_error', status);
      } else {
        error.value = t('signup.errors.generic');
        emitSignupError('server_error', status);
      }
    };

    // Alta por invitación: email + contraseña + el token de la invitación (y el
    // de Turnstile si está encendido). La cuenta nace confirmada y se entra.
    const handleRegister = async () => {
      if (esperandoCaptcha.value) return;
      loading.value = true;
      error.value = '';

      // signup_started: intento real de alta (formulario válido y enviado), no
      // apertura de la página ni foco en un campo. Es un micro-evento, NO una
      // conversión: sign_up solo se emite si la cuenta acaba creándose.
      okeyTrack('signup_started', { method: 'password' });
      // signup_submit_attempt: mismo instante, distinto propósito. Es el
      // denominador honesto de signup_error — cuántos envíos hubo, cuántos
      // fallaron — sin depender de que signup_started sobreviva a un rename.
      okeyTrack('signup_submit_attempt', { screen: 'register', step: 'credentials', method: 'password' });

      let alta: { data?: { sesion?: { access_token?: string; refresh_token?: string } | null } } | null = null;
      try {
        const localeForMeta = (typeof localStorage !== 'undefined' && localStorage.getItem('locale')) || 'es';
        alta = await api.post('/api/auth/signup/direct', {
          email: form.value.email,
          password: form.value.password,
          preferred_locale: localeForMeta,
          invitation_token: invitationToken.value,
          ...(captchaToken.value ? { captcha_token: captchaToken.value } : {}),
        });
      } catch (err: unknown) {
        tratarNegativa(err);
        loading.value = false;
        // El token de Turnstile ya se gastó: el siguiente intento necesita otro.
        turnstile.value?.reset?.();
        return;
      }
      try {
        await completeSignIn(alta?.data?.sesion ?? null);
      } finally {
        loading.value = false;
      }
    };

    // ------------------------------------------------------------------
    // Mount-time bootstrap: ?lang= from landing deeplink + signup_started
    // ------------------------------------------------------------------
    try {
      const url = new URL(window.location.href);
      const lang = url.searchParams.get('lang');
      if (lang && (SUPPORTED_LOCALES as readonly string[]).includes(lang)) {
        setLocale(lang as AppLocale);
      }
      const prefill = paramDeLaUrl('email');
      if (prefill) form.value.email = prefill;
      // Síncrono: quien trae invitación no ve parpadear «Solicitar acceso».
      invitationToken.value = invitationTokenFromUrl();
      if (invitationToken.value) {
        previewInvitation(invitationToken.value).then((vista) => {
          invitation.value = vista;
          if (vista) form.value.email = vista.email;
          else invitationDead.value = true;
        });
      }
      okeyTrack('auth_page_view', {
        screen: 'register',
        plan_interest: url.searchParams.get('plan_interest') || 'none',
        prefilled: !!prefill,
      });
    } catch {
      // ignore
    }
    // Ojo: este `signup_started` va al BACKEND (funnelApi), no a GA4/PostHog.
    // Comparte nombre con el que emite okeyTrack en el submit pero mide otra
    // cosa — aquí es "abrió el formulario", allí "lo envió".
    trackEvent({ event: 'signup_started', utm: gatherAttribution() });

    return {
      invitation,
      invitationDead,
      mostrarSolicitud,
      esperandoCaptcha,
      captchaToken,
      turnstile,
      signInPath,
      loading,
      error,
      formValid,
      showPassword,
      form,
      locales,
      onPickLocale,
      emailRules,
      passwordRules,
      termsRules,
      handleRegister,
    };
  },
});
</script>

<style scoped>
.register-page-shell {
  --ink:        var(--ii-ink);
  --ink-hushed: var(--ii-ink-2);
  --border:     var(--ii-rule);
  min-height: 100vh;
  background: var(--casa-fondo);
  color: var(--ink);
  font-family: var(--ii-display);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
}

.register-page-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--casa-espacio-8) var(--casa-espacio-6) 64px;
}

.register-page-card {
  width: 100%;
  max-width: 460px;
}

.register-page-logo { margin-bottom: var(--casa-espacio-2); }

.register-page-heading {
  margin: 0 0 var(--casa-espacio-3);
  font-family: inherit;
  font-size: 40px;
  line-height: 1.0;
  font-weight: 500;
  letter-spacing: -0.04em;
  color: var(--ink);
  text-wrap: balance;
}
@media (min-width: 768px) {
  .register-page-heading { font-size: 48px; }
}

.register-page-input { margin-bottom: var(--casa-espacio-3); }

.register-page-hint {
  margin: 0 0 var(--casa-espacio-2);
  font-size: var(--casa-letra-control);
  font-weight: 400;
  color: var(--ink-hushed);
}

.register-page-terms { margin: var(--casa-espacio-1) 0 var(--casa-espacio-2); padding: 0; }
.register-page-terms__label {
  font-family: inherit;
  font-size: var(--casa-letra-texto);
  font-weight: 400;
  color: var(--ink-hushed);
  line-height: 1.4;
}

.register-page-error {
  margin: 0 0 var(--casa-espacio-3);
  padding: var(--casa-espacio-2-5) var(--casa-espacio-3);
  background: var(--casa-fondo);
  border: 1px solid var(--casa-error);
  border-left: 3px solid var(--casa-error);
  border-radius: var(--casa-radio-tarjeta);
  color: var(--casa-error);
  font-size: var(--casa-letra-control);
  font-weight: 400;
}

.register-page-submit {
  margin-top: var(--casa-espacio-2);
  height: 46px !important;
  background-color: var(--ii-accent) !important;
  color: var(--casa-texto-inverso) !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  border-radius: var(--casa-radio-tarjeta) !important;
  box-shadow: none !important;
}
.register-page-submit:hover { background-color: var(--ii-accent-2) !important; }
.register-page-submit:disabled {
  background-color: var(--casa-fondo-app) !important;
  color: var(--casa-texto-tenue) !important;
}

.register-page-footer {
  margin-top: var(--casa-espacio-8);
  font-family: inherit;
  font-size: var(--casa-letra-texto);
  font-weight: 400;
  color: var(--ink-hushed);
  line-height: 1.5;
}
.register-page-link {
  color: var(--ink);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 400;
}
.register-page-arrow {
  display: inline-block;
  margin-left: var(--casa-espacio-0-5);
  transform: translateY(-1px);
}

.register-page-lang {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: var(--casa-espacio-1-5);
  font-size: var(--casa-letra-control);
  color: var(--ink-hushed);
}
.register-page-lang__label { font-weight: 400; }
.register-page-lang__link {
  color: var(--ink-hushed);
  text-decoration: none;
  font-weight: 400;
  padding: var(--casa-espacio-0-5) var(--casa-espacio-1);
}
.register-page-lang__link:hover { color: var(--ink); }
.register-page-lang__link--active {
  color: var(--ink);
  font-weight: 400;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Vuetify input theming → Ramp */
.register-page-input :deep(.v-input__slot) {
  min-height: 46px !important;
  border-radius: var(--casa-radio-tarjeta) !important;
}
.register-page-input :deep(fieldset) {
  border-color: var(--border) !important;
  border-width: 1px !important;
}
.register-page-input :deep(.v-text-field--outlined.v-input--is-focused fieldset) {
  border-color: var(--ii-accent) !important;
  border-width: 1px !important;
}
.register-page-input :deep(.v-label) {
  color: var(--ink-hushed) !important;
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
}
.register-page-input :deep(input) {
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  padding: var(--casa-espacio-2-5) var(--casa-espacio-1) !important;
  color: var(--ink) !important;
}
.register-page-input :deep(.v-icon) { color: var(--ink-hushed) !important; }

/* Checkbox accent → brand blue */
.register-page-terms :deep(.v-input--selection-controls__input .v-icon) {
  color: var(--ink-hushed) !important;
}
.register-page-terms :deep(.v-input--is-label-active .v-input--selection-controls__input .v-icon),
.register-page-terms :deep(.v-input--selection-controls__input .primary--text) {
  color: var(--ii-accent) !important;
}

@media (max-width: 480px) {
  .register-page-heading { font-size: 36px; }
  .register-page-main { padding: var(--casa-espacio-6) var(--casa-espacio-5); }
}
</style>
